/**
 * CR-7058: route-level enforcement of Navigation Menu Management.
 *
 * Wraps every protected page. If the current location belongs to a
 * navigation entry that the user's configuration has switched off — the admin
 * toggle in Platform Admin → Navigation Menu Management, the tenant's module
 * allowlist or the user's module permission — the page is replaced by
 * FeatureUnavailable. Direct URLs, bookmarks, notification deep-links and
 * in-app links all pass through here, so hiding the sidebar item is no longer
 * the only line of defence.
 *
 * Paths that no navigation entry owns (settings, help, notifications, admin
 * tooling, …) are untouched. Sub-pages inherit their parent's entry, so
 * `/quality/ncr/12/edit` follows the Quality toggle.
 *
 * While platform settings and the view context load for the first time the
 * page is held back behind a spinner rather than briefly rendering (and
 * fetching for) a page that is about to be refused. Both must be in: the
 * menu toggles come from platform settings, and which side of the portal
 * (customer / supplier) the user is on — so which entries exist at all —
 * comes from the view context (for a Helix admin, the company being viewed).
 * Evaluating before the view has loaded would resolve the wrong side and let
 * the page mount for a moment. Later reloads (site / company switch) keep the
 * page on screen and re-evaluate with the refreshed settings.
 */
import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { useView } from '../../contexts/ViewContext';
import { Loader2 } from 'lucide-react';
import { useSidebarNavigation } from './useSidebarNavigation';
import { resolveNavItemForPath, resolveSubToggleForPath } from './navigationAccess';
import FeatureUnavailable from './FeatureUnavailable';

interface NavigationAccessGateProps {
  children: ReactNode;
}

export default function NavigationAccessGate({ children }: NavigationAccessGateProps) {
  const { pathname } = useLocation();
  const { isLoading: menuSettingsLoading, purchasingTabs, qualityTabs } = useMenuSettings();
  const { isLoading: viewLoading } = useView();
  const {
    gateNavigation, navigation, isNavItemEnabledByConfig, isCarrierHiddenRoute,
  } = useSidebarNavigation();
  const isLoading = menuSettingsLoading || viewLoading;

  // Block only until the first load completes; a reload triggered by a scope
  // change must not blank the page the user is already on.
  const [settled, setSettled] = useState(!isLoading);
  useEffect(() => {
    if (!isLoading) setSettled(true);
  }, [isLoading]);

  if (!settled) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]" data-testid="navigation-access-loading">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Send the user somewhere they are allowed to be — the first entry that
  // survived the filters, or their account page if nothing did.
  const homeFor = (excludeHref?: string): string =>
    navigation.find(item => item.href !== excludeHref)?.href ?? '/settings';

  // A sub-toggle is checked first: /quality/approval-register must be refused
  // when the Approval Register tab is off, even while Quality itself is on.
  const sub = resolveSubToggleForPath(pathname, { purchasingTabs, qualityTabs });
  if (sub && !sub.enabled) {
    return <FeatureUnavailable featureName={sub.route.label} homeHref={homeFor()} />;
  }

  // CR-7732: a haulier holds no stock of its own, so the stock surfaces are
  // refused by address as well as hidden from its sidebar.
  if (isCarrierHiddenRoute(pathname)) {
    return <FeatureUnavailable featureName="Stock Levels" homeHref={homeFor(pathname)} />;
  }

  const owner = resolveNavItemForPath(gateNavigation, pathname);
  if (owner && !isNavItemEnabledByConfig(owner)) {
    return <FeatureUnavailable featureName={owner.name} homeHref={homeFor(owner.href)} />;
  }

  return <>{children}</>;
}
